import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getArticleBySlug, ARTICLES, formatPublishDate } from "../lib/articles";
import { SITE, WHATSAPP_LINK } from "../lib/site-data";
import {
  ArrowLeft,
  BookOpen,
  Calculator,
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  Calendar,
  MessageCircle,
  ArrowRight,
} from "lucide-react";

const SUBJECT_ICON = { English: BookOpen, Math: Calculator, Parents: Users };

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) return <Navigate to="/" replace />;

  const Icon = SUBJECT_ICON[article.subject] || BookOpen;
  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-body" data-testid="article-page">
      <Header />

      <main>
        <article className="pt-12 pb-10 lg:pt-20 lg:pb-14">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/#articles"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#475569] hover:text-[#1E3A8A] transition-colors mb-8"
              data-testid="article-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to articles
            </Link>

            <div className="inline-flex items-center gap-1.5 bg-white border border-[#E2E8F0] px-3 py-1 rounded-full text-xs font-medium text-[#1E3A8A] mb-5">
              <Icon className="w-3.5 h-3.5" />
              {article.subject} · PSLE
            </div>

            <h1
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight"
              data-testid="article-title"
            >
              {article.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-[#475569]">
              <span className="inline-flex items-center gap-1.5" data-testid="article-date">
                <Calendar className="w-4 h-4" />
                {formatPublishDate(article.publishedAt)}
              </span>
              <span className="text-[#E2E8F0]">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {article.readTime}
              </span>
              <span className="text-[#E2E8F0]">·</span>
              <span>{SITE.name}</span>
            </div>

            <div className="mt-10 rounded-3xl overflow-hidden border border-[#E2E8F0] bg-white">
              <img
                src={article.hero}
                alt=""
                aria-hidden="true"
                className="w-full h-64 sm:h-80 object-contain p-6 bg-white"
              />
            </div>

            <p className="mt-10 text-lg text-[#0F172A] leading-relaxed" data-testid="article-intro">
              {article.intro}
            </p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              <section
                className="rounded-3xl bg-white border border-[#E2E8F0] p-7"
                data-testid="article-dos"
              >
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-9 h-9 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-[#0F172A]">
                    Do&apos;s
                  </h2>
                </div>
                <ul className="space-y-3">
                  {article.dos.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#0F172A] leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section
                className="rounded-3xl bg-white border border-[#E2E8F0] p-7"
                data-testid="article-donts"
              >
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-9 h-9 rounded-2xl bg-[#D97706] text-white flex items-center justify-center">
                    <XCircle className="w-5 h-5" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-[#0F172A]">
                    Don&apos;ts
                  </h2>
                </div>
                <ul className="space-y-3">
                  {article.donts.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#0F172A] leading-relaxed">
                      <XCircle className="w-4 h-4 text-[#D97706] mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <p
              className="mt-12 text-base text-[#475569] leading-relaxed italic border-l-4 border-[#1E3A8A] pl-5"
              data-testid="article-closing"
            >
              {article.closing}
            </p>

            <div className="mt-12 rounded-3xl bg-[#1E3A8A] text-white p-8 sm:p-10">
              <h3 className="font-heading text-2xl font-semibold">
                Want personalised feedback on your child&apos;s writing or sums?
              </h3>
              <p className="mt-3 text-white/80 max-w-xl">
                Our P4–P6 classes are capped at 10 students with individual
                written feedback on every piece of work.
              </p>
              <a
                href={WHATSAPP_LINK(
                  `Hi ${SITE.name}, I read the article "${article.title}" and would like to enquire about classes for my child.`
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 bg-[#D97706] hover:bg-[#B45309] text-white px-6 py-3 rounded-full font-medium transition-all shadow-md hover:shadow-lg"
                data-testid="article-cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                Chat with us on WhatsApp
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="py-14 lg:py-20 bg-white border-t border-[#E2E8F0]" data-testid="related-articles">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading text-2xl font-semibold text-[#0F172A] mb-8">
                Read next
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {related.map((a) => {
                  const RIcon = SUBJECT_ICON[a.subject] || BookOpen;
                  return (
                    <Link
                      key={a.slug}
                      to={`/articles/${a.slug}`}
                      className="block bg-[#FAFAFA] rounded-2xl border border-[#E2E8F0] hover:border-[#1E3A8A]/40 p-6 transition-all group"
                      data-testid={`related-${a.slug}`}
                    >
                      <div className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1E3A8A] mb-3">
                        <RIcon className="w-3.5 h-3.5" />
                        {a.subject}
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-[#0F172A] group-hover:text-[#1E3A8A] transition-colors leading-snug">
                        {a.title}
                      </h3>
                      <p className="mt-2 text-sm text-[#475569] leading-relaxed line-clamp-2">
                        {a.summary}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
